import { round } from "lodash";
import {
  createContext,
  useContext,
  useState,
  ReactNode,
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
} from "react";

type LayoutContextType = {
  navbarHeightPx: number;
  setNavbarHeightPx: Dispatch<SetStateAction<number>>;
  navbarHeightRem: number;
  /**
   * The height of the banners the root pins at the top of every page
   * (maintenance, stale session, impersonation); 0 when none is showing. The
   * navbar, the admin rail and top bar, and anything sticky in an admin page
   * sit below it (Jira NAV-222, Jira NAV-227).
   */
  bannerStackHeightPx: number;
  setBannerStackHeightPx: Dispatch<SetStateAction<number>>;
};

const LayoutContext = createContext<LayoutContextType | undefined>(undefined);

export function LayoutProvider({ children }: { children: ReactNode }) {
  const [navbarHeightPx, setNavbarHeightPx] = useState(0);
  const [navbarHeightRem, setNavbarHeightRem] = useState(0);
  const [bannerStackHeightPx, setBannerStackHeightPx] = useState(0);

  useEffect(() => {
    const rootFontSize = parseFloat(
      window.getComputedStyle(document.documentElement).fontSize,
    );
    setNavbarHeightRem(round(navbarHeightPx / rootFontSize));
  }, [navbarHeightPx]);

  const value = useMemo(
    () => ({
      navbarHeightPx,
      setNavbarHeightPx,
      navbarHeightRem,
      bannerStackHeightPx,
      setBannerStackHeightPx,
    }),
    [navbarHeightPx, navbarHeightRem, bannerStackHeightPx],
  );

  return (
    <LayoutContext.Provider value={value}>{children}</LayoutContext.Provider>
  );
}

export function useLayout() {
  const context = useContext(LayoutContext);
  if (context === undefined) {
    throw new Error("useLayout must be used within a LayoutProvider");
  }
  return context;
}

/**
 * CSS min-height filling the viewport below the pinned banners and the fixed
 * navbar. Area layouts must use this instead of hardcoding the navbar height —
 * the root already offsets content by both measured heights (see __root.tsx),
 * so any overestimate produces a page a few pixels taller than the viewport
 * (a permanent tiny scrollbar).
 */
export function useContentMinHeight(): string {
  const { navbarHeightPx, bannerStackHeightPx } = useLayout();
  return `calc(100vh - ${navbarHeightPx + bannerStackHeightPx}px)`;
}
